import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { billingService } from "../services/billing.service";
import { tagOf } from "../utils/api-error";
import { formatAmount } from "../utils/money";
import { Plan, PendingPlanChange } from "../interfaces/billing.interface";

// Tags /billing/upgrade and /billing/downgrade document (docs/web-api-spec.md).
const KNOWN_TAGS = [
  "unknown_plan",
  "not_an_upgrade",
  "not_a_downgrade",
  "card_declined",
  "authentication_failed",
  "no_subscription",
  "stripe_unavailable",
];

type Props = {
  plans: Plan[];
  currentPlan: Plan;
  pendingChange: PendingPlanChange | null;
  onChanged: () => void;
};

// Plan switching for a self-paid subscriber. /pricing sends its Upgrade and
// Downgrade buttons here as ?plan=<code>, which opens that plan's confirm step.
const ChangePlan = ({ plans, currentPlan, pendingChange, onChanged }: Props) => {
  const { t, i18n } = useTranslation(["billing", "common"]);
  const lang = i18n.resolvedLanguage ?? "en";
  const [params] = useSearchParams();
  const [confirmingCode, setConfirmingCode] = useState<string | null>(
    params.get("plan"),
  );
  const [busy, setBusy] = useState(false);
  const [errorTag, setErrorTag] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    plan: string;
    kind: "upgrade" | "downgrade";
  } | null>(null);

  const others = plans.filter(
    (plan) => plan.tier !== "free" && plan.code !== currentPlan.code,
  );
  if (others.length === 0) return null;

  // Upgrade charges the prorated difference now; downgrade schedules the
  // switch for period end. Direction is by amount, matching the server guards.
  const onModify = async (plan: Plan, kind: "upgrade" | "downgrade") => {
    setErrorTag(null);
    setSuccess(null);
    setBusy(true);
    try {
      await (kind === "upgrade"
        ? billingService.upgrade(plan.code)
        : billingService.downgrade(plan.code));
      setConfirmingCode(null);
      setSuccess({ plan: plan.name, kind });
      onChanged();
    } catch (err) {
      setErrorTag(tagOf(err) ?? "generic");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="change-plan" className="space-y-3 rounded-2xl bg-sand p-4 text-sm">
      <p className="font-display font-extrabold">{t("account.changePlan")}</p>

      {errorTag && (
        <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-red-700">
          {KNOWN_TAGS.includes(errorTag)
            ? t(`errors.${errorTag}`)
            : t("common:errors.generic")}
        </p>
      )}
      {success && (
        <p data-testid="modify-success" aria-live="polite" className="text-teal-dark">
          {t(success.kind === "upgrade" ? "pricing.upgraded" : "pricing.downgraded", {
            plan: success.plan,
          })}
        </p>
      )}

      {others.map((plan) => {
        const kind: "upgrade" | "downgrade" | null =
          plan.amount > currentPlan.amount
            ? "upgrade"
            : plan.amount < currentPlan.amount
              ? "downgrade"
              : null;
        if (!kind) return null;
        const isScheduled = pendingChange?.planCode === plan.code;
        const confirming = confirmingCode === plan.code && !isScheduled;
        return (
          <div
            key={plan.code}
            data-testid={`plan-row-${plan.code}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-[1.5px] border-hairline bg-surface p-3"
          >
            <span>
              <span className="font-semibold">{plan.name}</span>{" "}
              <span className="text-ink-soft">
                {formatAmount(plan.amount, plan.currency, lang)}
                {plan.interval === "month" ? t("pricing.perMonth") : t("pricing.perYear")}
              </span>
            </span>
            {isScheduled ? (
              <button type="button" disabled className="btn-ghost">
                {t("pricing.ctaScheduled")}
              </button>
            ) : confirming ? (
              <span className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid={`confirm-${plan.code}`}
                  disabled={busy}
                  onClick={() => void onModify(plan, kind)}
                  className="btn-teal"
                >
                  {t(kind === "upgrade" ? "pricing.confirmUpgrade" : "pricing.confirmDowngrade")}
                </button>
                <button type="button" onClick={() => setConfirmingCode(null)} className="btn-ghost">
                  {t("common:actions.cancel")}
                </button>
              </span>
            ) : (
              <button
                type="button"
                data-testid={`cta-${plan.code}`}
                disabled={busy}
                onClick={() => setConfirmingCode(plan.code)}
                className="btn-ghost"
              >
                {t(kind === "upgrade" ? "pricing.ctaUpgrade" : "pricing.ctaDowngrade")}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ChangePlan;
